/**
 * Public candidate-facing immersion confirmation page.
 *
 * Reached via `/i/:token` (token embedded in the recap SMS/WhatsApp).
 * Single CTA: confirm presence. NO opt-out (per Shariti's velocity policy).
 *
 * Tracks two signals back to PREREC:
 *   • `link_viewed_at` on initial GET
 *   • `confirmed_at`   on CTA click
 *
 * After confirmation, the candidate can download an .ics file to add the
 * immersion to their personal calendar (client-side generation, free).
 */
import { motion, AnimatePresence } from "framer-motion";
import {
    Calendar,
    CalendarPlus,
    Check,
    ExternalLink,
    Loader2,
    MapPin,
    Phone,
    Sparkles,
    User,
    XCircle,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;


/* ──────────────────────────────────────────────────── .ics helper */

function buildIcs({ prenom, dateLong, heure, iso, lieu, referent_name, poste }) {
    // Generate a 1h-long immersion event.
    const start = new Date(iso);
    const end = new Date(start.getTime() + 60 * 60 * 1000);
    const fmt = (d) =>
        d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
    const uid = `${start.getTime()}-shariti-immersion`;
    const summary = `Entretien immersif · ${poste || "Shariti"}`;
    const description = `Bonjour ${prenom}, votre immersion avec ${referent_name || "votre référent"}.\\nLieu : ${lieu || ""}`;
    return [
        "BEGIN:VCALENDAR",
        "VERSION:2.0",
        "PRODID:-//Shariti//PREREC//FR",
        "CALSCALE:GREGORIAN",
        "METHOD:PUBLISH",
        "BEGIN:VEVENT",
        `UID:${uid}`,
        `DTSTAMP:${fmt(new Date())}`,
        `DTSTART:${fmt(start)}`,
        `DTEND:${fmt(end)}`,
        `SUMMARY:${summary}`,
        `LOCATION:${(lieu || "").replace(/\n/g, " ")}`,
        `DESCRIPTION:${description}`,
        "END:VEVENT",
        "END:VCALENDAR",
    ].join("\r\n");
}

function downloadIcs(data) {
    const ics = buildIcs(data);
    const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "entretien-immersif-shariti.ics";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}


/** Build a Google Calendar "render?action=TEMPLATE" URL with the event pre-filled. */
function buildGoogleCalendarUrl({ iso, lieu, referent_name, poste, prenom }) {
    if (!iso) return "#";
    const start = new Date(iso);
    const end = new Date(start.getTime() + 60 * 60 * 1000);
    const fmt = (d) =>
        d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
    const params = new URLSearchParams({
        action: "TEMPLATE",
        text: `Entretien immersif · ${poste || "Shariti"}`,
        dates: `${fmt(start)}/${fmt(end)}`,
        details: `Bonjour ${prenom || ""}, votre entretien immersif avec ${referent_name || "votre référent"}.`,
        location: lieu || "",
    });
    return `https://calendar.google.com/calendar/render?${params.toString()}`;
}


/* ──────────────────────────────────────────────────── Countdown */

function useCountdown(iso) {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const interval = setInterval(() => setNow(Date.now()), 60_000);
        return () => clearInterval(interval);
    }, []);
    return useMemo(() => {
        if (!iso) return null;
        const target = new Date(iso).getTime();
        const diff = target - now;
        if (diff <= 0) return { label: "Aujourd'hui", urgent: true };
        const days = Math.floor(diff / (24 * 3600 * 1000));
        const hours = Math.floor((diff % (24 * 3600 * 1000)) / (3600 * 1000));
        if (days === 0) return { label: `Dans ${hours}h`, urgent: true };
        if (days === 1) return { label: "Demain", urgent: true };
        return { label: `Dans ${days} jours`, urgent: days <= 3 };
    }, [iso, now]);
}


/* ──────────────────────────────────────────────────── Page */

export default function ImmersionConfirm() {
    const { token } = useParams();
    const [state, setState] = useState({ status: "loading", data: null, error: null });
    const [confirming, setConfirming] = useState(false);
    const [justConfirmed, setJustConfirmed] = useState(false);

    const fetchData = useCallback(async () => {
        try {
            const res = await fetch(`${BACKEND_URL}/api/public/immersion/${token}`);
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                setState({ status: "error", data: null, error: body.detail || "Lien invalide" });
                return;
            }
            const data = await res.json();
            setState({ status: "ready", data, error: null });
        } catch (_e) {
            setState({ status: "error", data: null, error: "Connexion impossible" });
        }
    }, [token]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleConfirm = async () => {
        if (confirming) return;
        setConfirming(true);
        try {
            const res = await fetch(`${BACKEND_URL}/api/public/immersion/${token}/confirm`, {
                method: "POST",
            });
            if (!res.ok) throw new Error("Confirmation impossible");
            const data = await res.json();
            setState({ status: "ready", data, error: null });
            setJustConfirmed(true);
        } catch (_e) {
            // Silent fail — keep state; user can retry
        } finally {
            setConfirming(false);
        }
    };

    const countdown = useCountdown(state.data?.iso_datetime);

    /* ─── Loading ─── */
    if (state.status === "loading") {
        return (
            <div className="min-h-screen bg-[#0d0a07] flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-[#ee722e]" />
            </div>
        );
    }

    /* ─── Error ─── */
    if (state.status === "error") {
        return (
            <div className="min-h-screen bg-[#0d0a07] flex items-center justify-center px-6">
                <div className="max-w-md w-full bg-[#1a130c] border border-white/5 rounded-3xl p-10 text-center" data-testid="immersion-error-card">
                    <XCircle className="w-14 h-14 text-red-400 mx-auto mb-4" strokeWidth={1.5} />
                    <h1 className="text-2xl font-black text-white mb-2 font-display">Lien invalide</h1>
                    <p className="text-white/60 text-sm leading-relaxed">
                        {state.error}<br />
                        Si vous pensez qu'il s'agit d'une erreur, contactez votre interlocuteur Shariti.
                    </p>
                </div>
            </div>
        );
    }

    /* ─── Ready / Confirmed ─── */
    const { prenom, poste, date_long, heure, lieu, notes, referent_name, referent_prenom, referent_avatar, referent_phone, confirmed_at, iso_datetime } = state.data;
    const isConfirmed = !!confirmed_at;
    const showSuccess = justConfirmed || (isConfirmed && !confirming);

    return (
        <div className="min-h-screen bg-[#0d0a07] text-white relative overflow-hidden">
            {/* Soft brand glow */}
            <div className="absolute inset-x-0 top-0 h-[60vh] bg-gradient-to-b from-[#ee722e]/25 via-[#ee722e]/5 to-transparent pointer-events-none" />
            <div className="absolute -top-32 -right-32 w-[400px] h-[400px] bg-[#ee722e]/20 rounded-full blur-[120px] pointer-events-none" />
            <div className="absolute -bottom-32 -left-32 w-[400px] h-[400px] bg-[#b94d1d]/15 rounded-full blur-[120px] pointer-events-none" />

            <div className="relative max-w-[640px] mx-auto px-5 sm:px-8 py-10 sm:py-14">
                {/* Brand strip */}
                <div className="flex items-center gap-2 mb-10">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#ee722e] to-[#b94d1d] flex items-center justify-center font-black text-white text-base shadow-[0_4px_20px_rgba(238,114,46,0.4)]">
                        S
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Shariti</div>
                        <div className="text-sm font-bold text-white/90 -mt-0.5">Entretien immersif</div>
                    </div>
                </div>

                <AnimatePresence mode="wait">
                    {showSuccess ? (
                        /* ───────── Success state ───────── */
                        <motion.div
                            key="success"
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ type: "spring", stiffness: 260, damping: 24 }}
                            data-testid="immersion-success-card"
                        >
                            <div className="relative bg-gradient-to-br from-[#1a130c] to-[#120c08] border border-emerald-400/20 rounded-[28px] p-8 sm:p-12 text-center">
                                <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ type: "spring", stiffness: 300, damping: 18, delay: 0.1 }}
                                    className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-[0_10px_40px_rgba(52,211,153,0.4)] mb-6"
                                >
                                    <Check className="w-10 h-10 text-white" strokeWidth={3} />
                                </motion.div>
                                <div className="text-xs uppercase tracking-[0.2em] text-emerald-300/80 font-bold mb-2">
                                    Présence confirmée
                                </div>
                                <h1 className="text-3xl sm:text-4xl font-black text-white font-display leading-tight mb-3">
                                    À très vite, {prenom} !
                                </h1>
                                <p className="text-white/60 text-base leading-relaxed mb-8 max-w-md mx-auto">
                                    L'équipe Shariti vous attend {date_long?.toLowerCase()} à {heure}.
                                    {referent_prenom ? ` ${referent_prenom} sera votre référent terrain.` : ""}
                                </p>

                                {/* Mini-recap */}
                                <div className="bg-black/30 border border-white/5 rounded-2xl p-5 mb-6 text-left space-y-3">
                                    <div className="flex items-center gap-3">
                                        <Calendar className="w-4 h-4 text-[#ee722e] shrink-0" />
                                        <div className="text-sm text-white/85"><span className="text-white font-semibold">{date_long}</span> · {heure}</div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <MapPin className="w-4 h-4 text-[#ee722e] shrink-0" />
                                        <div className="text-sm text-white/85">{lieu || "Lieu à confirmer"}</div>
                                    </div>
                                    {referent_name && (
                                        <div className="flex items-center gap-3">
                                            <User className="w-4 h-4 text-[#ee722e] shrink-0" />
                                            <div className="text-sm text-white/85">Avec {referent_name}</div>
                                        </div>
                                    )}
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                                    <button
                                        onClick={() => downloadIcs({ prenom, dateLong: date_long, heure, iso: iso_datetime, lieu, referent_name, poste })}
                                        className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-white/10 hover:bg-white/15 text-white font-semibold text-sm transition border border-white/10"
                                        data-testid="immersion-download-ics-btn"
                                    >
                                        <CalendarPlus className="w-4 h-4" />
                                        Fichier .ics (Apple, Outlook…)
                                    </button>
                                    <a
                                        href={buildGoogleCalendarUrl({ iso: iso_datetime, lieu, referent_name, poste, prenom })}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-white/10 hover:bg-white/15 text-white font-semibold text-sm transition border border-white/10"
                                        data-testid="immersion-google-calendar-btn"
                                    >
                                        <ExternalLink className="w-4 h-4" />
                                        Ajouter à Google Calendar
                                    </a>
                                </div>

                                <div className="mt-8 text-xs text-white/35">
                                    Confirmé le {new Date(confirmed_at || Date.now()).toLocaleString("fr-FR", { timeZone: "Europe/Paris", dateStyle: "long", timeStyle: "short" })}
                                </div>
                            </div>
                        </motion.div>
                    ) : (
                        /* ───────── Confirm state ───────── */
                        <motion.div
                            key="ready"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.4 }}
                        >
                            {/* Hero */}
                            <div className="mb-8">
                                {countdown && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4 ${countdown.urgent
                                            ? "bg-[#ee722e]/20 text-[#ffb27a] border border-[#ee722e]/30"
                                            : "bg-white/5 text-white/60 border border-white/10"
                                            }`}
                                    >
                                        <Sparkles className="w-3 h-3" />
                                        {countdown.label}
                                    </motion.div>
                                )}
                                <h1 className="text-4xl sm:text-5xl font-black text-white font-display leading-[1.05] mb-3" data-testid="immersion-hero-title">
                                    Bonjour {prenom},<br />
                                    <span className="text-[#ee722e]">on vous attend.</span>
                                </h1>
                                <p className="text-white/55 text-base sm:text-lg leading-relaxed max-w-lg">
                                    Vous êtes convié(e) à votre <span className="text-white/80 font-semibold">entretien immersif</span> pour le poste de <span className="text-white/80 font-semibold">{poste}</span>.
                                </p>
                            </div>

                            {/* Details card */}
                            <div className="bg-gradient-to-br from-[#1a130c] to-[#120c08] border border-white/8 rounded-[24px] p-6 sm:p-7 space-y-5 mb-6" data-testid="immersion-details-card">
                                {/* Date + heure */}
                                <div className="flex items-start gap-4">
                                    <div className="w-11 h-11 rounded-xl bg-[#ee722e]/15 border border-[#ee722e]/25 flex items-center justify-center shrink-0">
                                        <Calendar className="w-5 h-5 text-[#ee722e]" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="text-[11px] uppercase tracking-wider text-white/45 font-semibold mb-1">Quand</div>
                                        <div className="text-white font-bold text-lg leading-tight">{date_long}</div>
                                        <div className="text-white/70 text-sm">à {heure}</div>
                                    </div>
                                </div>

                                <div className="h-px bg-white/5" />

                                {/* Lieu */}
                                <div className="flex items-start gap-4">
                                    <div className="w-11 h-11 rounded-xl bg-[#ee722e]/15 border border-[#ee722e]/25 flex items-center justify-center shrink-0">
                                        <MapPin className="w-5 h-5 text-[#ee722e]" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-[11px] uppercase tracking-wider text-white/45 font-semibold mb-1">Où</div>
                                        <div className="text-white font-bold text-base leading-tight break-words">{lieu || "Lieu à confirmer"}</div>
                                        {lieu && (
                                            <a
                                                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(lieu)}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-block mt-1.5 text-xs text-[#ee722e] hover:text-[#ffb27a] font-semibold underline-offset-2 hover:underline transition"
                                                data-testid="immersion-map-link"
                                            >
                                                Voir sur Google Maps →
                                            </a>
                                        )}
                                    </div>
                                </div>

                                {referent_name && (
                                    <>
                                        <div className="h-px bg-white/5" />
                                        <div className="flex items-start gap-4">
                                            <div className="w-11 h-11 rounded-xl bg-[#ee722e]/15 border border-[#ee722e]/25 flex items-center justify-center shrink-0 overflow-hidden">
                                                {referent_avatar ? (
                                                    <img src={referent_avatar} alt="" className="w-full h-full object-cover" />
                                                ) : (
                                                    <User className="w-5 h-5 text-[#ee722e]" />
                                                )}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="text-[11px] uppercase tracking-wider text-white/45 font-semibold mb-1">Votre référent</div>
                                                <div className="text-white font-bold text-base leading-tight">{referent_name}</div>
                                                <div className="text-white/55 text-xs mt-0.5">vous accompagnera sur le terrain</div>
                                                {referent_phone && (
                                                    <a
                                                        href={`tel:${referent_phone.replace(/\s/g, "")}`}
                                                        className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-lg bg-[#ee722e]/10 hover:bg-[#ee722e]/20 border border-[#ee722e]/30 text-[#ffb27a] hover:text-white text-xs font-bold transition"
                                                        data-testid="immersion-referent-phone"
                                                    >
                                                        <Phone className="w-3 h-3" />
                                                        {referent_phone}
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* What to bring */}
                            <div className="bg-black/30 border border-white/5 rounded-2xl p-5 mb-8">
                                <div className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-bold mb-3">À prévoir</div>
                                <ul className="space-y-2 text-sm text-white/75">
                                    <li className="flex gap-2.5"><span className="text-[#ee722e]">•</span> Tenue confortable adaptée à la marche</li>
                                    <li className="flex gap-2.5"><span className="text-[#ee722e]">•</span> Une pièce d'identité</li>
                                    <li className="flex gap-2.5"><span className="text-[#ee722e]">•</span> Votre meilleur sourire 💪</li>
                                </ul>
                                {notes && (
                                    <div className="mt-4 pt-4 border-t border-white/5 text-sm text-white/70 leading-relaxed whitespace-pre-line">
                                        {notes}
                                    </div>
                                )}
                            </div>

                            {/* Main CTA + agreement caption */}
                            <div className="bg-[#ee722e]/8 border border-[#ee722e]/20 rounded-2xl p-4 mb-5">
                                <div className="flex gap-2.5 items-start text-xs text-white/75 leading-relaxed">
                                    <Check className="w-4 h-4 text-[#ee722e] shrink-0 mt-0.5" strokeWidth={2.5} />
                                    <div>
                                        En cliquant sur <span className="text-white font-bold">Je confirme ma présence</span>, je valide les <span className="text-white font-bold">conditions de la mission</span> que nous avons revues ensemble en entretien téléphonique (disponibilités, durée, mobilité, rémunération…).
                                    </div>
                                </div>
                            </div>

                            <motion.button
                                onClick={handleConfirm}
                                disabled={confirming}
                                whileTap={{ scale: 0.98 }}
                                className="w-full relative group overflow-hidden rounded-[20px] bg-gradient-to-br from-[#ee722e] via-[#d66528] to-[#b94d1d] text-white font-black text-base sm:text-lg uppercase tracking-wider py-5 sm:py-6 shadow-[0_20px_60px_-15px_rgba(238,114,46,0.6)] disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                                data-testid="immersion-confirm-btn"
                            >
                                <span className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                                <span className="relative flex items-center justify-center gap-2.5">
                                    {confirming ? (
                                        <>
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                            Envoi en cours…
                                        </>
                                    ) : (
                                        <>
                                            <Check className="w-5 h-5" strokeWidth={3} />
                                            Je confirme ma présence
                                        </>
                                    )}
                                </span>
                            </motion.button>

                            <p className="text-center text-xs text-white/40 mt-4 leading-relaxed">
                                Une équipe se mobilise pour vous accueillir. À très bientôt !
                            </p>
                        </motion.div>
                    )}
                </AnimatePresence>

                <div className="mt-12 text-center text-[10px] uppercase tracking-[0.2em] text-white/25">
                    Powered by Shariti · Le cockpit du recrutement terrain
                </div>
            </div>
        </div>
    );
}
